import React, { useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import useColumnState from "../../hooks/useColumnState";

/**
 * Reusable AgGrid component with persistent column state management
 * @param {object} props - AgGridReact props plus additional configuration
 * @param {string} props.tableId - Unique identifier for the table to store state
 * @param {function} props.onGridReady - Custom onGridReady handler (optional)
 * @param {object} props.defaultColDef - Default column definitions (will be merged with column state settings)
 * @param {boolean} props.enableColumnState - Whether to enable column state persistence (default: true)
 * @param {object} props.gridApiRef - External ref for grid API (optional)
 * @returns {JSX.Element} AgGridReact component with column state management
 */
const AgGridWithColumnState = ({
  tableId,
  onGridReady: customOnGridReady,
  onFirstDataRendered: customOnFirstDataRendered,
  onFilterChanged: customOnFilterChanged,
  onSortChanged: customOnSortChanged,
  onColumnMoved: customOnColumnMoved,
  onColumnResized: customOnColumnResized,
  onColumnVisible: customOnColumnVisible,
  onColumnPinned: customOnColumnPinned,
  defaultColDef = {},
  enableColumnState = true,
  gridApiRef: externalGridApiRef,
  columnDefs,
  ...agGridProps
}) => {
  const {
    gridApiRef,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onColumnEvent,
    onFilterChanged: handleFilterChanged,
    savedColumnState,
  } = useColumnState(enableColumnState ? tableId : null);

  // CRITICAL: Reapply column state when columnDefs change OR when savedColumnState changes
  // (e.g., after login when user ID becomes available)
  useEffect(() => {
    if (
      enableColumnState &&
      gridApiRef.current &&
      savedColumnState &&
      columnDefs &&
      Array.isArray(savedColumnState) &&
      savedColumnState.length > 0
    ) {
      // Small delay to ensure grid has processed new columnDefs
      const timeoutId = setTimeout(() => {
        try {
          if (gridApiRef.current) {
            gridApiRef.current.applyColumnState({
              state: savedColumnState,
              applyOrder: true,
              defaultState: { sort: null, sortIndex: null },
            });
          }
        } catch (error) {
          console.warn(
            "Failed to reapply column state after columnDefs/savedColumnState change:",
            error
          );
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [columnDefs, enableColumnState, savedColumnState]);

  // Enhanced default column definitions with column state support
  const enhancedDefaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 50,
    lockPosition: false, // Allow column moving
    ...defaultColDef,
  };

  // Combined grid ready handler
  const handleOnGridReady = (params) => {
    // Set external ref if provided
    if (externalGridApiRef) {
      externalGridApiRef.current = params.api;
    }

    // Handle column state management if enabled
    if (enableColumnState) {
      handleGridReady(params);
    }

    // Call custom onGridReady if provided
    if (customOnGridReady) {
      customOnGridReady(params);
    }
  };

  // Combined first data rendered handler
  const handleOnFirstDataRendered = (params) => {
    // Handle column state management if enabled
    if (enableColumnState) {
      handleFirstDataRendered(params);
    }

    // Call custom onFirstDataRendered if provided
    if (customOnFirstDataRendered) {
      customOnFirstDataRendered(params);
    }
  };

  // Combined filter changed handler - saves filter state and calls custom handler
  const handleOnFilterChanged = (params) => {
    // Save filter state if enabled
    if (enableColumnState && handleFilterChanged) {
      handleFilterChanged(params);
    }
    // Call custom handler if provided
    if (customOnFilterChanged) {
      customOnFilterChanged(params);
    }
  };

  // Combined sort changed handler - saves column state and calls custom handler
  const handleOnSortChanged = (params) => {
    // Save column state (which includes sort) if enabled
    if (enableColumnState && onColumnEvent) {
      onColumnEvent(params);
    }
    // Call custom handler if provided
    if (customOnSortChanged) {
      customOnSortChanged(params);
    }
  };

  // Column moved handler - saves column state and calls custom handler
  const handleOnColumnMoved = (params) => {
    if (enableColumnState && onColumnEvent) {
      onColumnEvent(params);
    }
    if (customOnColumnMoved) {
      customOnColumnMoved(params);
    }
  };

  // Column resized handler - saves column state and calls custom handler
  const handleOnColumnResized = (params) => {
    if (enableColumnState && onColumnEvent) {
      onColumnEvent(params);
    }
    if (customOnColumnResized) {
      customOnColumnResized(params);
    }
  };

  // Column visible handler - saves column state and calls custom handler
  const handleOnColumnVisible = (params) => {
    if (enableColumnState && onColumnEvent) {
      onColumnEvent(params);
    }
    if (customOnColumnVisible) {
      customOnColumnVisible(params);
    }
  };

  // Column pinned handler - saves column state and calls custom handler
  const handleOnColumnPinned = (params) => {
    if (enableColumnState && onColumnEvent) {
      onColumnEvent(params);
    }
    if (customOnColumnPinned) {
      customOnColumnPinned(params);
    }
  };

  return (
    <AgGridReact
      {...agGridProps}
      columnDefs={columnDefs}
      defaultColDef={enhancedDefaultColDef}
      suppressMovableColumns={!enableColumnState}
      animateRows={false}
      onGridReady={handleOnGridReady}
      onFirstDataRendered={handleOnFirstDataRendered}
      onColumnMoved={handleOnColumnMoved}
      onColumnResized={handleOnColumnResized}
      onColumnVisible={handleOnColumnVisible}
      onColumnPinned={handleOnColumnPinned}
      onSortChanged={handleOnSortChanged}
      onFilterChanged={handleOnFilterChanged}
    />
  );
};

export default AgGridWithColumnState;
